/**
 * aiJumpLinks — 粮小助回答内「菜单名」自动变跳转链接 (2026-09-28)
 *
 * 原理: 生产环境烘干厂前端与 MaxKB 同源 (hgc.liangyiagri.com, /chat/ 反代),
 * 主页面可直接读写 iframe DOM。这里对聊天页挂 MutationObserver,
 * 扫描回答 markdown 区 (.ai-chat__content .md-editor-preview) 的文本节点,
 * 把其中出现的菜单名包成 <a data-aijump>, 点击后由 AiHelperBall 执行 router.push 并收起抽屉。
 *
 * 边界:
 * - 跨源 iframe (本地 dev: localhost:3100 嵌 127.0.0.1:8088) 读不到 contentDocument,
 *   installAiJump 静默降级为无跳转, 纯文本展示。
 * - MaxKB 流式输出会反复重绘 .md-editor-preview 的 innerHTML, 靠防抖重扫兜住;
 *   已包过的文本在 <a> 内, 重扫天然跳过 (幂等)。
 * - 只动 md-editor-preview (v-html 渲染区, Vue 不做 vnode diff), 不碰 MaxKB 自己管理的节点。
 * - 权限: 挂 perm 的菜单按 store.permKeys 过滤 (无 perm = 全员可见);
 *   permKeys 未加载时放行, 越权由路由守卫拦截 (安全兜底在守卫)。
 */
import { ALL_ITEMS } from '@/config/menuStructure'
import store from '@/store'

/** 语义别名: 回答里的旧叫法/泛称 → 实际菜单 key (不在菜单表里的常用落点也在此补) */
const LABEL_ALIASES = {
  商品管理: 'product', // 客户口径的「商品管理」= 商品档案页 (sys 组重名菜单 factory-product 不参与)
  过磅设备: 'weigh-device', // 旧叫法「系统管理→过磅设备」已拆到「设备管理→设备配置」
  教程中心: 'tutorial',
  个人中心: 'profile',
  通知中心: 'notice'
}

/** 组装 label → { key, path, title, perm }; 重名取先出现的 (如「资金结算表」财务页优先于报表页) */
function buildLabelMap() {
  const map = {}
  const push = (label, it) => {
    if (label && !map[label]) {
      map[label] = { key: it.key, path: '/' + it.key, title: it.label, perm: it.perm || null }
    }
  }
  ALL_ITEMS.forEach(it => {
    // legacy=废弃入口 / placeholder=建设中空壳 / superOnly=平台超管专属: 均不参与跳转
    if (it.legacy || it.placeholder || it.superOnly || it.hidden) return
    if (LABEL_ALIASES[it.label]) return // label 被语义别名接管 (如 factory-product 的「商品管理」让位给商品档案)
    push(it.label, it)
  })
  Object.keys(LABEL_ALIASES).forEach(label => {
    const it = ALL_ITEMS.find(x => x.key === LABEL_ALIASES[label]) ||
      { key: LABEL_ALIASES[label], perm: null } // tutorial/profile/notice 等公开路由不在 ALL_ITEMS
    push(label, it)
  })
  return map
}

const LABEL_MAP = buildLabelMap()
const LABELS = Object.keys(LABEL_MAP).sort((a, b) => b.length - a.length) // 长名优先, 防「粮食入库汇总」截胡「粮食入库汇总表」
const LABEL_SRC = LABELS.map(s => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|')

/** 注入到聊天页 <head> 的链接样式 (幂等, 按 id 判重; 加 !important 压过 md-editor 主题的 a 颜色) */
const STYLE_ID = 'ai-jump-link-style'
const STYLE_CSS =
  '.md-editor-preview a.ai-jump-link,.ai-jump-link{color:#2f7d32 !important;' +
  'text-decoration:underline dotted !important;cursor:pointer;font-weight:inherit;}' +
  '.md-editor-preview a.ai-jump-link:hover,.ai-jump-link:hover{color:#1e5e21 !important;' +
  'text-decoration-color:#1e5e21 !important;}'

/**
 * 扫描 root 下所有回答 markdown 区, 把文本节点里的菜单名包成跳转链接 (幂等)。
 */
function wrapAll(doc) {
  const previews = doc.querySelectorAll('.ai-chat__content .md-editor-preview')
  const perms = store.state.permKeys || []
  previews.forEach(pv => {
    const walker = doc.createTreeWalker(pv, NodeFilter.SHOW_TEXT, {
      acceptNode(node) {
        const text = node.textContent
        if (!text || !text.trim()) return NodeFilter.FILTER_REJECT
        const p = node.parentElement
        if (!p || p.closest('a')) return NodeFilter.FILTER_REJECT // 已包过/本来就是链接
        if (/^(SCRIPT|STYLE|TEXTAREA|INPUT|CODE|PRE)$/.test(p.tagName)) return NodeFilter.FILTER_REJECT
        return new RegExp(LABEL_SRC).test(text) ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_REJECT
      }
    })
    const hit = []
    while (walker.nextNode()) hit.push(walker.currentNode)
    hit.forEach(node => wrapTextNode(doc, node, perms))
  })
}

/** 把单个文本节点按命中词切片, 命中处替换为 <a class=ai-jump-link> */
function wrapTextNode(doc, node, perms) {
  const text = node.textContent
  const re = new RegExp(LABEL_SRC, 'g')
  const frag = doc.createDocumentFragment()
  let last = 0
  let m
  let wrapped = false
  while ((m = re.exec(text))) {
    const item = LABEL_MAP[m[0]]
    if (!item) continue
    if (item.perm && perms.length && !perms.includes(item.perm)) continue
    if (m.index > last) frag.appendChild(doc.createTextNode(text.slice(last, m.index)))
    const a = doc.createElement('a')
    a.className = 'ai-jump-link'
    a.dataset.aijump = item.path
    a.href = '#ai-jump'
    a.title = '点击打开「' + item.title + '」页面'
    a.textContent = m[0]
    frag.appendChild(a)
    last = m.index + m[0].length
    wrapped = true
  }
  if (!wrapped) return
  if (last < text.length) frag.appendChild(doc.createTextNode(text.slice(last)))
  node.parentNode.replaceChild(frag, node)
}

/**
 * 在 MaxKB 聊天 iframe 内安装「菜单名跳转」。
 * @param {HTMLIFrameElement} frame 抽屉里的聊天 iframe
 * @param {Object} handlers { onJump(path) } 点击链接时回调 (执行 router.push + 收起抽屉)
 * @returns {Function} uninstall 清理函数
 */
export function installAiJump(frame, handlers) {
  let doc
  try {
    doc = frame && frame.contentDocument
    if (!doc || !doc.body) throw new Error('iframe document 不可访问')
  } catch (e) {
    // 跨源 (本地 dev 等非同源部署): 静默降级, 回答保持纯文本
    // eslint-disable-next-line no-console
    console.info('[aiJump] iframe 跨源, 菜单跳转不可用:', (e && e.message) || e)
    return () => {}
  }
  if (doc.__aiJumpInstalled) return () => {}
  doc.__aiJumpInstalled = true

  // 链接样式
  if (!doc.getElementById(STYLE_ID)) {
    const style = doc.createElement('style')
    style.id = STYLE_ID
    style.textContent = STYLE_CSS
    doc.head.appendChild(style)
  }

  // 点击委托: 只拦自己的链接, 不干扰 MaxKB 的建议按钮/复制按钮等
  const onClick = e => {
    const a = e.target && e.target.closest && e.target.closest('a[data-aijump]')
    if (!a) return
    e.preventDefault()
    e.stopPropagation()
    if (typeof handlers.onJump === 'function') handlers.onJump(a.dataset.aijump)
  }
  doc.addEventListener('click', onClick, false)

  // 流式输出/新消息 → 防抖重扫
  let timer = null
  const scheduleScan = () => {
    if (timer) clearTimeout(timer)
    timer = setTimeout(() => {
      timer = null
      try {
        wrapAll(doc)
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn('[aiJump] 扫描失败', err)
      }
    }, 250)
  }
  const observer = new doc.defaultView.MutationObserver(scheduleScan)
  observer.observe(doc.body, { childList: true, subtree: true, characterData: true })

  wrapAll(doc) // 安装时已渲染的历史消息立即处理

  return function uninstall() {
    if (timer) clearTimeout(timer)
    observer.disconnect()
    doc.removeEventListener('click', onClick, false)
    doc.__aiJumpInstalled = false
  }
}
